import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Package,
  FolderTree,
  Clock,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useImport, useImportStatus, type StoreSettings } from '../hooks/useStoreSettings'
import { toast } from 'sonner'

interface SyncButtonProps {
  settings: StoreSettings | undefined
}

export function SyncButton({ settings }: SyncButtonProps) {
  const [isPolling, setIsPolling] = useState(false)

  const importMutation = useImport()
  const { data: importStatus } = useImportStatus(isPolling)

  // Start polling when import is triggered
  useEffect(() => {
    if (importMutation.isSuccess) {
      setIsPolling(true)
    }
  }, [importMutation.isSuccess])

  // Stop polling when import completes or errors
  useEffect(() => {
    if (importStatus?.status === 'complete') {
      setIsPolling(false)
      toast.success(importStatus.message || 'Import complete!')
    } else if (importStatus?.status === 'error') {
      setIsPolling(false)
      toast.error(importStatus.error || 'Import failed')
    }
  }, [importStatus?.status])

  const handleSync = async () => {
    try {
      await importMutation.mutateAsync(true)
      toast.info('Import started...')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start import')
    }
  }

  const isImporting =
    importMutation.isPending ||
    importStatus?.status === 'running' ||
    // Legacy compatibility (kept just in case any old in-flight syncs use these)
    importStatus?.status === 'fetching' ||
    importStatus?.status === 'indexing'

  // Workflow doesn't know the final total until it's done, so we don't try
  // to render a percentage bar. Just show the rolling count instead.
  const progressPercent = 0

  // Format last sync time
  const lastSyncDisplay = settings?.lastSyncAt
    ? formatDistanceToNow(settings.lastSyncAt * 1000, { addSuffix: true })
    : 'Never'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Product Sync
        </CardTitle>
        <CardDescription>
          Sync products and categories from WooCommerce
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
            <Package className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">{settings?.productCount || 0}</p>
              <p className="text-xs text-muted-foreground">Products</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
            <FolderTree className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">{settings?.categoryCount || 0}</p>
              <p className="text-xs text-muted-foreground">Categories</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
            <Clock className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-medium truncate">{lastSyncDisplay}</p>
              <p className="text-xs text-muted-foreground">Last Sync</p>
            </div>
          </div>
        </div>

        {/* Import Progress (workflow-driven, no fixed total) */}
        {isImporting && importStatus && (
          <div className="space-y-2 p-3 rounded-lg border border-border bg-muted/30">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                Syncing…
              </span>
              <span className="text-muted-foreground">
                {importStatus.progress > 0 ? `${importStatus.progress} synced` : ''}
              </span>
            </div>
            {/* Indeterminate-style progress bar — animates while we work */}
            <Progress value={progressPercent} className="h-2 animate-pulse" />
            <p className="text-xs text-muted-foreground">{importStatus.message}</p>
          </div>
        )}

        {/* Import Complete — hide while a new sync is being kicked off */}
        {importStatus?.status === 'complete' && !isImporting && !importMutation.isPending && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-5 w-5" />
            <span className="text-sm">{importStatus.message}</span>
          </div>
        )}

        {/* Import Error */}
        {importStatus?.status === 'error' && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">{importStatus.error}</span>
          </div>
        )}

        {/* Sync Button */}
        <Button
          onClick={handleSync}
          disabled={isImporting || !settings?.wooKeySet || !settings?.wooSecretSet}
          className="w-full"
        >
          {isImporting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Sync Now
            </>
          )}
        </Button>

        {!settings?.wooKeySet || !settings?.wooSecretSet ? (
          <p className="text-xs text-center text-muted-foreground">
            Configure WooCommerce credentials above to enable sync
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
